'use client';

import React, { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useLoading } from '@/contexts/LoadingContext';
import { getLanguageFromBrowserCookies } from '@/lib/languageUtils';
import { LanguageType } from '@/lib/types';

const LoadErrorBanner: React.FC = () => {
	const { isLoadTimedOut } = useLoading();
	const pathname = usePathname();
	const [language, setLanguage] = useState<LanguageType>('english');
	const [isDismissed, setIsDismissed] = useState(false);
	const hasLoggedRef = useRef(false);

	useEffect(() => {
		setLanguage(getLanguageFromBrowserCookies());
	}, []);

	// Log the error once when the banner appears
	useEffect(() => {
		if (!isLoadTimedOut || hasLoggedRef.current) return;
		hasLoggedRef.current = true;

		fetch('/api/log-load-error', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ pathname }),
		}).catch(() => {});
	}, [isLoadTimedOut, pathname]);

	// Reset logged flag and dismissed state on pathname change
	useEffect(() => {
		hasLoggedRef.current = false;
		setIsDismissed(false);
	}, [pathname]);

	const isExcludedRoute = pathname.startsWith('/booking') || pathname === '/login';
	if (!isLoadTimedOut || isDismissed || isExcludedRoute) return null;

	return (
		<div
			className="
				fixed
				z-[10001]
				bottom-0
				left-1/2
				-translate-x-1/2
				w-[calc(100%-1.5rem)]
				max-w-[360px]
				tb:left-auto
				tb:translate-x-0
				tb:right-[3rem]
				tb:bottom-[3rem]
				tb:w-fit
				tb:max-w-none
			"
		>
			<div
				className="
					bg-bg-light-tertiary
					border-secondary
					border-stroke-soft-200
					border-b-transparent
					tb:border-b-stroke-soft-200
					rounded-tl-[20px]
					rounded-tr-[20px]
					tb:rounded-bl-[20px]
					tb:rounded-br-[20px]
					flex
					flex-col
					tb:flex-row
					items-center
					gap-[1rem]
					tb:gap-[2rem]
					py-[1.5rem]
					px-[2rem]
					tb:py-[0.75rem]
					tb:px-[1.5rem]
				"
			>
				<p className="
					text-16-fixed--alexandria
					text-text-tertiary
					whitespace-normal
					tb:whitespace-nowrap
					text-center
					tb:text-left
				">
					{language === 'arabic'
						? 'لم يتم تحميل هذه الصفحة بشكل صحيح. يرجى التحديث أو المحاولة لاحقًا.'
						: "This page didn't load correctly. Please refresh or try again later."}
				</p>

				<div className="flex flex-col tb:flex-row w-full tb:w-auto gap-[1rem]">
					<button
						onClick={() => window.location.reload()}
						className="
							w-full
							tb:w-auto
							text-16-fixed--alexandria
							text-text-tertiary
							bg-transparent
							border-[1.5px]
							border-stroke-soft-200
							rounded-[16px]
							py-[12px]
							px-[22px]
							cursor-pointer
							transition-colors
							duration-300
							hover:bg-bg-light-quaternary
							hover:border-stroke-sub-300
							whitespace-nowrap
						"
					>
						{language === 'arabic' ? 'تحديث' : 'Refresh'}
					</button>

					<button
						onClick={() => setIsDismissed(true)}
						className="
							w-full
							tb:w-auto
							text-16-fixed--alexandria
							text-text-tertiary
							bg-transparent
							border-[1.5px]
							border-stroke-soft-200
							rounded-[16px]
							py-[12px]
							px-[22px]
							cursor-pointer
							transition-colors
							duration-300
							hover:bg-bg-light-quaternary
							hover:border-stroke-sub-300
							whitespace-nowrap
						"
					>
						{language === 'arabic' ? 'حسنًا' : 'Okay'}
					</button>
				</div>
			</div>
		</div>
	);
};

export default LoadErrorBanner;
